import { Box, Button, CircularProgress, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { useEffect, useReducer } from 'react';
import constants from '../../config/constants';
import { useLanguage } from '../../context/language.context';
import MonthSelectionBox from './MonthSelectionBox';
import { initialState, reducer } from './reducer';

const Payments = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { payments } = state;
    const { t, dir } = useLanguage();

    useEffect(() => {
        const fetchPayments = async () => {
            try {
                dispatch({ type: 'CLEAR_ERROR' });
                dispatch({ type: 'START_LOADING' });
                const res = await axios.get(`${constants.API_URL}/instructor/admin/private_appointments/summary`, {
                    params: { filter: state.date },
                });
                dispatch({ type: 'SET_PAYMENTS', payload: res.data });
            } catch (error) {
                dispatch({ type: 'SET_IS_ERROR' });
            } finally {
                dispatch({ type: 'FINISH_LOADING' });
            }
        };
        fetchPayments();
    }, [state.date]);
    const refreshPayments = async () => {
        const res = await axios.get(`${constants.API_URL}/instructor/admin/private_appointments/summary/update`, {
            params: { filter: state.date },
        });
        dispatch({ type: 'SET_PAYMENTS', payload: res.data });
        dispatch({ type: 'CLEAR_ERROR' });
    };
    const paymentRows: { title: string; count?: number; price?: number; info?: string; notes?: string }[] = [
        {
            title: t('consultationSessions'),
            count: state.payments?.consultationSessions.count,
            price: state.payments?.consultationSessions.price,
            info: state.payments?.consultationSessions.info,
            notes:""
        },
        {
            title: t('oneToOneSessionsLabel'),
            count: state.payments?.oneToOneSessions.count,
            price: state.payments?.oneToOneSessions.price,
            info: t('clearingFeeNote'),
            notes:""
        },
        {
            title: t('groupSessionsLabel'),
            count: state.payments?.groupSessions.count,
            price: state.payments?.groupSessions.price,
            info: state.payments?.groupSessions.info,
            notes:""
        },
        {
            title: t('safeSpace'),
            count: state.payments?.safe_space.count,
            price: state.payments?.safe_space.price,
            info: state.payments?.safe_space.info,
            notes: state.payments?.notes,
        },
    ];

    // Budget-subsidy ("תקציב ניהולי") row — only when there are such sessions
    // (i.e. when the WELLNESS_PAYMENT_SPLIT flag is on server-side).
    if (state.payments?.wellness?.count) {
        paymentRows.push({
            title: t('wellnessSessionsLabel'),
            count: state.payments.wellness.count,
            price: state.payments.wellness.price,
            info: state.payments.wellness.info,
            notes: '',
        });
    }
    return (
        <Box pt={3}>
            <MonthSelectionBox
                onResetMonthClick={() => {
                    dispatch({ type: 'SET_DATE', padyload: new Date() });
                }}
                onNextMonthClick={() => {
                    dispatch({ type: 'NEXT_MONTH' });
                }}
                onPrevMonthClick={() => {
                    dispatch({ type: 'PREV_MONTH' });
                }}
                month={state.date.month}
                year={state.date.year}
            />

            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                {t('bankDetailsNote')}
                <br />
                {t('paymentInvoiceNote')}
            </Typography>

            <Box textAlign='center'>
                {state.loading ? (
                    <CircularProgress />
                ) : state.isError ? (
                    <Box>
                        <h2>{`${t('noDataForMonth')} ${state.date.month + 1}/${state.date.year}`}</h2>
                        <Button onClick={refreshPayments}>{t('updateData')}</Button>
                    </Box>
                ) : (
                    <div>
                        <Table style={{ direction: dir }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell width={200}></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paymentRows.map((row, index) => (
                                    <TableRow key={`row#${row.title}`}>
                                        <TableCell>{row.title}</TableCell>
                                        <TableCell>{row.count}</TableCell>
                                        <TableCell>{`${row.price}\t₪`}</TableCell>
                                        <TableCell className='whitespace-pre-wrap'>{row.info}
                                {' '}
                               <span style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize:13 }}> {index == 3 && t('clearingFeeFullNote')}</span>

                                        </TableCell>

                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell>{t('total')}</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>{`${payments?.total?.price || 0}\t₪`}</TableCell>
                                    {moment({ year: state.date.year, month: state.date.month, date: 1 }).isAfter(moment({ year: 2025, month: 8, date: 1 })) && (
                                        <TableCell className='whitespace-pre-wrap'>{payments?.notes}</TableCell>
                                    )}
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography variant='caption'>
                                            {t('vatNote')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                        <div
                            style={{
                                direction: dir,
                                display: 'flex',
                                alignItems: 'center',
                            }}>
                            <span style={{ marginInlineEnd: 20 }}>
                                <Typography variant='caption'>{`${t('lastUpdated')} ${moment(state.payments?.updatedAt).fromNow()}`}</Typography>
                            </span>
                            <span>
                                <Tooltip title={moment().diff(state.payments?.updatedAt, 'hour') < 1 ? t('canUpdateOncePerHour') : ''}>
                                    <span>
                                        <Button size='small' onClick={refreshPayments} disabled={moment().diff(state.payments?.updatedAt, 'hour') < 1}>
                                            {t('updateData')}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </span>
                        </div>
                    </div>
                )}
            </Box>

        </Box>
    );
};

export default Payments;
